import React, { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import {
    CaretRightFilled,
    PauseOutlined
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import './VideoCard.css';

const VideoCard = ({ image, isSelected }) => {
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [volume, setVolume] = useState(0.8);
    const [played, setPlayed] = useState(0);
    const [duration, setDuration] = useState(0);
    const [seeking, setSeeking] = useState(false);
    const [showControls, setShowControls] = useState(false);
    const playerRef = useRef(null);
    const containerRef = useRef(null);

    // Helper to format time
    const formatTime = (seconds) => {
        const date = new Date(seconds * 1000);
        const hh = date.getUTCHours();
        const mm = date.getUTCMinutes();
        const ss = date.getUTCSeconds().toString().padStart(2, '0');
        if (hh) {
            return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
        }
        return `${mm}:${ss}`;
    };

    const handlePlayPause = (e) => {
        e.stopPropagation();
        setPlaying(!playing);
    };

    const handleProgress = (state) => {
        // Only update progress if we are not currently seeking
        if (!seeking) {
            setPlayed(state.played);
        }
    };

    const handleDuration = (duration) => {
        setDuration(duration);
    };

    const handleSeekChange = (e) => {
        setPlayed(parseFloat(e.target.value));
    };

    const handleSeekMouseDown = () => {
        setSeeking(true);
    };

    const handleSeekMouseUp = (e) => {
        setSeeking(false);
        playerRef.current.seekTo(parseFloat(e.target.value));
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        setMuted(!muted);
    };

    const toggleFullscreen = (e) => {
        e.stopPropagation();
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else if (containerRef.current.requestFullscreen) {
            containerRef.current.requestFullscreen();
        }
    };

    // Extract Metadata
    const videoTitle = image.display_name || "Untitled";

    const getResolutionLabel = (resString) => {
        if (!resString) return "1280x720 1K MP4"; // Fallback

        try {
            const [w, h] = resString.toLowerCase().split('x').map(Number);
            if (!w || !h) return `${resString} MP4`;

            let label = "SD";
            if (w >= 3840) label = "4K";
            else if (w >= 2560) label = "2K"; // QHD
            else if (w >= 1280) label = "1K"; // HD/FHD (following user convention)

            return `${w}x${h} ${label} MP4`;
        } catch (e) {
            return `${resString} MP4`;
        }
    };

    const resolution = getResolutionLabel(image.resolution);

    // Calculate progress bar background
    const progressPercent = played * 100;
    const sliderStyle = {
        background: `linear-gradient(to right, #fff ${progressPercent}%, rgba(255,255,255,0.3) ${progressPercent}%)`
    };

    return (
        <div
            className={`video-card-container ${playing ? 'playing' : 'paused'} ${isSelected ? 'selected' : ''}`}
            ref={containerRef}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
            onClick={!playing ? handlePlayPause : undefined} // Click anywhere to play if paused
        >
            {image.status === 'inprogress' ? (
                <div className="video-thumbnail-wrapper">
                    <img
                        src={image.thumbnail || image.src}
                        alt={videoTitle}
                        className="video-thumbnail-img"
                        style={{ filter: "blur(4px)" }}
                    />
                </div>
            ) : (
                <>
                    <div className="video-thumbnail-wrapper">
                        <img
                            src={image.thumbnail || image.src}
                            alt={videoTitle}
                            className="video-thumbnail-img"
                        />
                        {/* Play Button - Only show if Player is NOT active */}
                        {(!playing && played === 0) && (
                            <div className="center-controls">
                                <button className="center-play-btn" onClick={handlePlayPause}>
                                    <CaretRightFilled className="play-icon-filled" />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* "Video X" Badge - Show whenever paused (including initial state) */}
                    {(!playing && image.video_number) && (
                        <div className="engine-logo-badge">
                            <span className="engine-name">Video {image.video_number}</span>
                        </div>
                    )}

                    {/* Player Overlay (Conditionally rendered) */}
                    {(playing || played > 0) && (
                        <div className="react-player-wrapper" onClick={(e) => { e.stopPropagation(); /* prevent click propagation */ }}>
                            <ReactPlayer
                                ref={playerRef}
                                url={image.videoUrl || image.src || image.mp4Preview}
                                width="100%"
                                height="100%"
                                playing={playing}
                                muted={muted}
                                volume={volume}
                                onProgress={handleProgress}
                                onDuration={handleDuration}
                                onEnded={() => setPlaying(false)}
                                style={{ objectFit: 'contain' }}
                                config={{
                                    file: {
                                        attributes: {
                                            style: {
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                backgroundColor: 'black'
                                            }
                                        }
                                    }
                                }}
                            />

                            {/* Overlays */}
                            <div className={`video-overlay-controls ${showControls ? 'visible' : ''}`}>
                                {/* Center Play/Pause */}
                                <div className="center-controls">
                                    {!playing ? (
                                        <button className="center-play-btn" onClick={handlePlayPause}>
                                            <CaretRightFilled className="play-icon-filled" />
                                        </button>
                                    ) : (
                                        <button className="center-play-btn" onClick={handlePlayPause}>
                                            <PauseOutlined className="pause-icon-outlined" />
                                        </button>
                                    )}
                                </div>

                                <div className="hover-info" style={{ opacity: 1, position: 'absolute', bottom: 0, padding: 0 }}>
                                    <div className="image-banner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '8px', padding: '10px' }}>
                                        {/* Top Row: Title and Resolution */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                            <Tooltip title={<span className="manrope f-12 white">{videoTitle}</span>}>
                                                <p className="manrope f-12 w-700 lh-140 clamp-text w-100 image-banner-text justify-in-start" style={{ margin: 0, color: 'white' }}>
                                                    {videoTitle}
                                                </p>
                                            </Tooltip>
                                            <p className="manrope f-12 image-banner-text" style={{ margin: 0, whiteSpace: 'nowrap', color: 'white' }}>
                                                {resolution}
                                            </p>
                                        </div>

                                        {/* Progress Bar Row */}
                                        <div className="slider-container" style={{ width: '100%' }}>
                                            <input
                                                type="range"
                                                min={0}
                                                max={0.999999}
                                                step="any"
                                                value={played}
                                                onMouseDown={handleSeekMouseDown}
                                                onChange={handleSeekChange}
                                                onMouseUp={handleSeekMouseUp}
                                                className="video-slider"
                                                style={sliderStyle}
                                            />
                                        </div>

                                        {/* Bottom Row: Time and Controls */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <div className="time-display manrope f-12" style={{ color: 'white' }}>{formatTime(duration * played)}</div>
                                            <div className="controls-right-group">
                                                <button className="icon-btn" onClick={toggleMute}>
                                                    {muted ?
                                                        <img src="/img/muted-icon.svg" alt="Unmute" className="custom-control-icon" /> :
                                                        <img src="/img/sound-icon.svg" alt="Mute" className="custom-control-icon" />
                                                    }
                                                </button>
                                                <button className="icon-btn" onClick={toggleFullscreen}>
                                                    <img src="/img/full-screen-video-icon.svg" alt="Fullscreen" className="custom-control-icon" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default VideoCard;
